import React, { useState, useEffect, useMemo } from 'react';
import { Offcanvas, Table, Card, Spinner, Alert, Badge, Row, Col, Button, Collapse, Tabs, Tab, Modal } from 'react-bootstrap';
import axios from 'axios';
import dayjs from 'dayjs';
import { FiInfo, FiFileText, FiHash, FiCalendar, FiExternalLink, FiBriefcase, FiChevronDown, FiChevronRight, FiCheckCircle, FiEye, FiDownload, FiPrinter } from 'react-icons/fi';
import ExpenseModal from '../project/expense/ExpenseModal';
import TaxInvoicePreview from '../ProformaInvoice/TaxInvoicePreview';
import { saveAs } from 'file-saver';
import Swal from 'sweetalert2';

const LedgerDetailsOffcanvas = ({ show, onHide, ledger, startDate, endDate, projectId }) => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [invoices, setInvoices] = useState([]);
    const [expandedInvoices, setExpandedInvoices] = useState(new Set());
    const [entryDetails, setEntryDetails] = useState({});
    const [fetchingEntry, setFetchingEntry] = useState(null);
    const [webzashStatement, setWebzashStatement] = useState(null);
    const [loadingStatement, setLoadingStatement] = useState(false);
    const [calculatedBalance, setCalculatedBalance] = useState(null);
    const [activeTab, setActiveTab] = useState('summary');
    const [resolvedLedger, setResolvedLedger] = useState(null);
    const [pendingOnly, setPendingOnly] = useState(false);
    const [ledgerDebug, setLedgerDebug] = useState(null);
    const [isLiabilityLedger, setIsLiabilityLedger] = useState(false);
    const [isReceivableLedger, setIsReceivableLedger] = useState(false);
    const [breakdownData, setBreakdownData] = useState({
        opening: [],
        debits: [],
        credits: [],
        closing: []
    });
    const [advanceBreakdown, setAdvanceBreakdown] = useState([]);
    const [drilldownData, setDrilldownData] = useState(null);

    // Detail States
    const [showExpenseModal, setShowExpenseModal] = useState(false);
    const [selectedExpenseId, setSelectedExpenseId] = useState(null);
    const [selectedExpenseData, setSelectedExpenseData] = useState(null);
    const [isEditMode, setIsEditMode] = useState(false);

    const [showPreviewModal, setShowPreviewModal] = useState(false);
    const [selectedInvoice, setSelectedInvoice] = useState(null);
    const [selectedQuotation, setSelectedQuotation] = useState(null);
    const [customIntegration, setCustomIntegration] = useState(false);
    const [customIntegrationDescription, setCustomIntegrationDescription] = useState('');
    const [editableDeploymentIntegration, setEditableDeploymentIntegration] = useState({});

    const [showDocOffcanvas, setShowDocOffcanvas] = useState(false);
    const [docUrl, setDocUrl] = useState('');
    const [downloadUrl, setDownloadUrl] = useState('');
    const [docTitle, setDocTitle] = useState('');

    const [users, setUsers] = useState([]);
    const [expenseTypes, setExpenseTypes] = useState([]);

    const API_BASE_URL = process.env.REACT_APP_WEBZASH_URL;
    const API_KEY = process.env.REACT_APP_API_KEY;
    const API_ORIGIN = process.env.REACT_APP_BACKEND_URL;

    useEffect(() => {
        if (show && ledger) {
            setResolvedLedger(ledger);
            const lType = determineLedgerType(ledger);
            const lName = (ledger?.name || '').toLowerCase();

            const isLiabilityType = ['vendor', 'liability', 'expense', 'employee', 'sundry_creditors', 'loan', 'equity', 'current_liability'].includes(lType) || 
                                   lName.includes('loan') || lName.includes('payable') || lName.includes('creditor');
            const isReceivableType = ['client', 'sundry_debtors', 'income', 'asset', 'bank', 'cash', 'current_asset'].includes(lType) || 
                                    lName.includes('bank') || lName.includes('cash') || lName.includes('receivable');

            const isSpecialLedger = (isLiabilityType || isReceivableType) && !startDate;

            setPendingOnly(!!isSpecialLedger);
            setIsLiabilityLedger(isLiabilityType);
            setIsReceivableLedger(isReceivableType);

            fetchData(isLiabilityType && !startDate);
        }
    }, [show, ledger, startDate, endDate, projectId]);

    const fetchData = async (forcePendingOnly = null) => {
        setLoading(true);
        setError(null);
        setInvoices([]);
        setExpandedInvoices(new Set());
        setEntryDetails({});
        setWebzashStatement(null);
        setActiveTab('summary');
        setBreakdownData({ opening: [], debits: [], credits: [], closing: [] });
        setAdvanceBreakdown([]);
        setCalculatedBalance(null);

        try {
            const userInfo = JSON.parse(localStorage.getItem('user-info'));
            const origin = localStorage.getItem('user-origin');
            const token = userInfo?.token;
            const headers = { Authorization: `Bearer ${token}`, 'Tenant-Origin': origin };

            const cleanLedgerName = ledger.name.replace(/\s*\(.*?\)\s*$/, '').trim();
            const ledgerType = determineLedgerType(ledger);

            // 1. CALL UNIFIED BACKEND API
            console.log(`[Drilldown] Fetching UNIFIED DRILLDOWN for: "${cleanLedgerName}"`);
            
            const drilldownResponse = await axios.get(`${API_ORIGIN}/api/ledger-drilldown`, {
                headers,
                params: {
                    ledger_id: ledger.id,
                    ledger_name: cleanLedgerName,
                    ledger_type: ledgerType,
                    start_date: startDate,
                    end_date: endDate,
                    projectId
                }
            });

            if (drilldownResponse.data?.status === 'success') {
                const drilldown = drilldownResponse.data.data;
                setDrilldownData(drilldownResponse.data); // Store full response for totals/debug
                
                // SHADOW FETCH FOR CLOSING LIST:
                // For BS accounts, the closing list should be cumulative (ignoring start_date).
                // We do a secondary fetch for just the closing bucket to avoid summary discrepancies.
                let fullClosingList = drilldown.closing || [];
                
                // Broaden detection to match Balance Sheet account behaviors (Vendors, Employees, Assets, etc.)
                const lName = (ledger?.name || '').toLowerCase();
                const isLiabilityType = ['vendor', 'liability', 'expense', 'employee', 'sundry_creditors', 'loan', 'equity', 'current_liability', 'advance_vendor'].includes(ledgerType) || 
                                       lName.includes('loan') || lName.includes('payable') || lName.includes('creditor');
                
                const isReceivableType = ['client', 'receivable', 'sundry_debtors', 'income', 'asset', 'bank', 'cash', 'current_asset', 'advance_client'].includes(ledgerType) || 
                                        lName.includes('bank') || lName.includes('cash') || lName.includes('receivable');
                
                if (startDate && (isLiabilityType || isReceivableType)) {
                    try {
                        const shadowResponse = await axios.get(`${API_ORIGIN}/api/ledger-drilldown`, {
                            headers,
                            params: {
                                ledger_id: ledger.id,
                                ledger_name: cleanLedgerName,
                                ledger_type: ledgerType,
                                start_date: '1970-01-01', // Force full history from the beginning
                                end_date: endDate, 
                                pending_only: 1, // Only get outstanding items for closing
                                projectId
                            }
                        });
                        const shadowData = shadowResponse.data?.data || shadowResponse.data;
                        if (shadowData.closing && Array.isArray(shadowData.closing)) {
                            fullClosingList = shadowData.closing;
                            console.log('[Drilldown] Shadow fetch successful (Deep History). Replaced closing list.', fullClosingList.length);
                        }
                    } catch (e) {
                        console.warn('[Drilldown] Shadow fetch failed, falling back to period closing list.', e);
                    }
                }

                // Prepare the combined list (Opening + Closing) BEFORE using it in state updates
                const combinedClosing = [
                    ...(drilldown.opening || []),
                    ...(fullClosingList || [])
                ];

                const unique = (list) => {
                    if (!list || !Array.isArray(list)) return [];
                    return list.filter((item, index, self) => 
                        index === self.findIndex((t) => t.id === item.id)
                    );
                };

                setBreakdownData({
                    opening: unique(drilldown.opening),
                    debits: unique(drilldown.debits),
                    credits: unique(drilldown.credits),
                    closing: unique(combinedClosing)
                });
                
                if (drilldown.advance_breakdown) {
                    setAdvanceBreakdown(unique(drilldown.advance_breakdown));
                }


                if (drilldown.webzash_statement) {
                    setWebzashStatement(drilldown.webzash_statement);
                }

                // Use the reconciled combined list as the source of truth for the list view
                const allItems = combinedClosing
                    .filter((item, index, self) => 
                        index === self.findIndex((t) => t.id === item.id)
                    ).sort((a, b) => dayjs(b.date).unix() - dayjs(a.date).unix());

                setInvoices(allItems);
            }
            setLoading(false);
            return; // EXIT EARLY - Rest of old function is replaced by this
        } catch (err) {
            console.error('[Drilldown] Error fetching data:', err);
            setError(err.message || 'Error loading transaction history');
            setLoading(false);
        }
    };

    // Keep old complex logic below for now but it will be unreachable 
    // or we can delete it in next step.

    const fetchWebzashStatement = async (targetLedger, populateInvoices = false) => {
        // Use provided ledger or fallback to state
        const activeLedger = targetLedger || resolvedLedger || ledger;

        // If we don't have an ID, we can't fetch the statement
        if (!activeLedger?.id) return;

        setLoadingStatement(true);
        try {
            const params = new URLSearchParams();
            const lType = determineLedgerType(activeLedger);
            // For Balance Sheet items (Liabilities, Assets, Equity), we often want the Cumulative Closing Balance as of EndDate.
            // If we send startDate, we get the period movement, but Closing Balance *should* still be correct (Opening + Movement).
            // However, to align with the "Up To" transaction list view where we skipped startDate, let's also skip it here if it's that type.
            // Actually, if we skip startDate, Webzash might default to FY start or Beginning.
            // Let's match the fetchData logic: Skip startDate for Liability/Asset ledgers to ensure we get the full "Up To" picture.

            const isBSLedger = ['liability', 'vendor', 'sundry_creditors', 'employee', 'fixed_asset', 'payroll', 'vat_payable', 'capital', 'retained_earnings', 'bank', 'cash', 'advance'].includes(lType) || lType === 'expense' || activeLedger.name.includes('payable') || activeLedger.name.includes('receivable');

            // If it's a BS ledger (Liability/Asset), we want Cumulative Balance Up To EndDate.
            // Sending 'startDate' limits it to the period.
            // To get true cumulative, we should send a 'start of time' date.
            // If we omit it, some APIs default to FY Start. To be safe, let's explicitely ask for history.
            if (startDate) {
                params.append('startdate', startDate);
                params.append('start_date', startDate);
            }
            if (endDate) {
                params.append('enddate', endDate);
                params.append('end_date', endDate);
            }

            // Webzash report endpoint handles both ledgers and groups via ledger_id/group_id
            if (activeLedger.is_group) {
                params.append('group_id', activeLedger.id);
            } else {
                params.append('ledger_id', activeLedger.id);
            }

            console.log(`[Drilldown] Fetching Webzash statement for ${activeLedger.is_group ? 'group' : 'ledger'} ID: ${activeLedger.id}`);
            const response = await axios.get(`${API_BASE_URL}/api/reports/ledgerstatement`, {
                headers: {
                    'x-api-key': API_KEY,
                    'Accept': 'application/json'
                },
                params
            });

            if (response.data) {
                const data = response.data;
                const entries = data.entries || [];

                // Calculate totals if they are 0 or missing
                let totalDr = parseFloat(data.period_totals?.dr_total || data.total_debit || data.total_dr || 0);
                let totalCr = parseFloat(data.period_totals?.cr_total || data.total_credit || data.total_cr || 0);

                if (totalDr === 0 && totalCr === 0 && entries.length > 0) {
                    entries.forEach(e => {
                        const amt = parseFloat(e.Entryitem?.amount || 0);
                        if (e.Entryitem?.dc === 'D') totalDr += amt;
                        else totalCr += amt;
                    });
                }

                const enrichedData = {
                    ...data,
                    total_debit: totalDr,
                    total_credit: totalCr
                };

                setWebzashStatement(enrichedData);

                // Populate transaction summary table from Webzash entries
                if (populateInvoices === 'advance' || (populateInvoices === true && lType === 'advance')) {
                    processAdvanceStatement(enrichedData, activeLedger);
                } else if (populateInvoices === 'general' || (populateInvoices === true && lType !== 'advance')) {
                    processGeneralStatement(enrichedData, activeLedger);
                }
            }
        } catch (err) {
            console.error('Error fetching Webzash statement:', err);
        } finally {
            setLoadingStatement(false);
        }
    };

    const processGeneralStatement = (statementData, activeLedger) => {
        try {
            const entries = statementData.entries || [];

            // Filter only valid lines for this ledger
            let relevantLines = entries;
            if (!activeLedger.is_group) {
                relevantLines = entries.filter(e => String(e.Entryitem?.ledger_id) === String(activeLedger.id));
            }

            const mappedItems = relevantLines.map(item => {
                const amt = parseFloat(item.Entryitem?.amount || 0);
                return {
                    id: item.Entry.id,
                    ref_no: item.Entry.reference_no || item.Entry.number || `ENT-${item.Entry.id}`,
                    project_name: item.Entry.tag_name || (item.Entry.tag_id ? `Tag: ${item.Entry.tag_id}` : ''),
                    date: item.Entry.date,
                    transactions: [{
                        id: item.Entry.id,
                        date: item.Entry.date,
                        amount: amt,
                        display_amount: amt,
                        description: item.Entry.narration,
                        type: 'journal',
                        journal_id: item.Entry.id,
                        external_id: item.Entry.number,
                        dc: item.Entryitem?.dc,
                        status: 'journal'
                    }],
                    display_amount: amt,
                    status: 'journal'
                };
            });

            setInvoices(mappedItems.sort((a, b) => dayjs(b.date).unix() - dayjs(a.date).unix()));
        } catch (err) {
            console.error("Error processing general statement:", err);
        } finally {
            setLoadingStatement(false);
        }
    };

    const processAdvanceStatement = (statementData, activeLedger) => {
        try {
            const entries = statementData.entries || [];
            const closingDC = statementData.closing_balance?.dc || 'D';

            // Group by Entity (Narration usually contains the name or we can extract it)
            // But better to use the ledger name if it's a specific ledger.
            // If it's a group, we want the sub-ledgers.
            
            const entityMap = {};

            entries.forEach(line => {
                if (!line.Entryitem) return;
                const ledgerId = line.Entryitem.ledger_id;
                const ledgerName = line.Entryitem.ledger_name;
                const amt = parseFloat(line.Entryitem.amount || 0);
                const dc = line.Entryitem.dc;

                if (!entityMap[ledgerId]) {
                    entityMap[ledgerId] = {
                        id: ledgerId,
                        name: ledgerName,
                        advance_amount: 0,
                        used_amount: 0,
                        balance: 0,
                        dc: ''
                    };
                }

                if (closingDC === 'D') {
                    if (dc === 'D') entityMap[ledgerId].advance_amount += amt;
                    else entityMap[ledgerId].used_amount += amt;
                } else {
                    if (dc === 'C') entityMap[ledgerId].advance_amount += amt;
                    else entityMap[ledgerId].used_amount += amt;
                }
            });

            const entityList = Object.values(entityMap).map(e => ({
                ...e,
                balance: Math.abs(e.advance_amount - e.used_amount)
            })).filter(e => e.balance > 0.01);

            setAdvanceBreakdown(entityList);
            
            // Standard FIFO logic follows...
            const sources = [];
            let offsetsTotal = 0;

            // Sort entries/lines by date ASC to apply FIFO correctly
            // The API usually returns them sorted, but good to ensure.
            // Note: statementData.entries is array of { Entry: {}, Entryitem: {} }

            // Filter only valid lines for this ledger
            // If it's a specific ledger, we filter strictly to avoid contra-entries if they exist.
            // If it's a group, we can't filter by ID easily (child IDs unknown), so we trust the API returned relevant lines.
            let relevantLines = entries;

            if (!activeLedger.is_group) {
                relevantLines = entries.filter(e => String(e.Entryitem.ledger_id) === String(activeLedger.id));
            }

            relevantLines = relevantLines.sort((a, b) => dayjs(a.Entry.date).unix() - dayjs(b.Entry.date).unix());

            console.log(`[Drilldown] Processing Advance Statement. Total Entries: ${entries.length}`, activeLedger);

            // Debug: Check IDs
            if (entries.length > 0) {
                console.log(`[Drilldown] First entry ID: ${entries[0].Entryitem?.ledger_id} vs Active Ledger ID: ${activeLedger.id}`);
            }

            relevantLines.forEach(line => {
                if (!line.Entryitem) return;

                // Strict Filter for Single Ledger
                // We MUST filter out contra lines (Bank/Expense) otherwise FIFO nets to 0.
                if (!activeLedger.is_group && String(line.Entryitem.ledger_id) !== String(activeLedger.id)) {
                    return;
                }

                // For Groups, we currently accept all lines (Risky - improvement needed if Group View is common)
                // But usually Drilldown is on Single Ledger.

                const amt = parseFloat(line.Entryitem.amount || 0);
                const dc = (line.Entryitem.dc || '').toUpperCase(); // Normalize DC

                // Determine if Source or Offset
                if (closingDC === 'D') {
                    if (dc === 'D') sources.push({ ...line, remaining: amt });
                    else offsetsTotal += amt;
                } else {
                    if (dc === 'C') sources.push({ ...line, remaining: amt });
                    else offsetsTotal += amt;
                }
            });

            console.log(`[Drilldown] Netting Info - ClosingDC: ${closingDC}, Sources: ${sources.length}, Offsets Total: ${offsetsTotal}`);

            // 2. Perform FIFO Netting
            const outstandingItems = [];
            for (const src of sources) {
                if (offsetsTotal >= src.remaining - 0.01) { // Tolerance for float precision
                    offsetsTotal -= src.remaining;
                    src.remaining = 0;
                    // Fully consumed
                } else {
                    src.remaining -= offsetsTotal;
                    offsetsTotal = 0;
                    if (src.remaining > 0.01) {
                        outstandingItems.push(src);
                    }
                }
            }

            console.log(`[Drilldown] Post-Netting Outstanding Items: ${outstandingItems.length}`);

            // 3. Map to Invoice/Transaction format for display
            const mappedItems = outstandingItems.map(item => ({
                id: item.Entry.id,
                ref_no: item.Entry.reference_no || item.Entry.number || `ENT-${item.Entry.id}`,
                project_name: item.Entry.tag_id ? `Tag: ${item.Entry.tag_id}` : '',
                date: item.Entry.date,
                transactions: [{
                    id: item.Entry.id,
                    date: item.Entry.date,
                    amount: parseFloat(item.Entryitem.amount),
                    pending_balance: item.remaining,
                    display_amount: item.remaining, // IMPORTANT: Use remaining for display
                    description: item.Entry.narration,
                    type: 'advance',
                    ref_no: item.Entry.reference_no
                }],
                display_amount: item.remaining,
                status: 'pending'
            }));

            console.log(`[Drilldown] Calculated ${mappedItems.length} outstanding advance items from statement.`);
            setInvoices(mappedItems.sort((a, b) => dayjs(b.date).unix() - dayjs(a.date).unix()));

        } catch (err) {
            console.error("Error processing advance statement:", err);
        }
    };

    // Helper function to determine ledger type from ledger data
    const determineLedgerType = (ledger) => {
        if (!ledger) return 'other';

        const ledgerName = ledger.name?.toLowerCase() || '';
        const groupName = ledger.group_name?.toLowerCase() || '';

        // Prioritize specific name-based routing for Sales Income/Returns
        // This must come before the explicit ledger.type check because sections like P&L pass generic types.
        if (ledgerName.includes('sales return')) return 'sales_return';
        if (ledgerName.includes('sales income')) return 'sales_income';

        // Check explicit type first if available and specific (Passed from P&L or other reports)
        if (ledger.type === 'vendor' || ledger.type === 'client' || ledger.type === 'expense' || ledger.type === 'income') {
            return ledger.type;
        }

        // Critical: Check for Bank Guarantees first
        if (ledgerName.includes('bank guarantee') || groupName.includes('bank guarantee') || ledgerName.includes('security deposit')) {
            return 'bank_guarantee';
        }

        // Restore Bank and Cash Checks
        if (groupName.includes('bank') || ledgerName.includes('bank account')) {
            return 'bank';
        }
        if (groupName.includes('cash') || ledgerName === 'cash' || ledgerName === 'petty cash') {
            return 'cash';
        }


        if (groupName.includes('sundry creditor') || groupName.includes('accounts payable')) {
            return 'vendor';
        }
        if (groupName.includes('sundry debtor') || groupName.includes('accounts receivable')) {
            return 'client';
        }

        // Strict Advance Check - Prioritize before generic Vendor/Client check if name clearly indicates advance
        if (ledgerName.includes('advance') || groupName.includes('advance') || ledgerName.includes('prepayment')) {
            return 'advance';
        }

        // Explicit Income/Revenue check
        if (groupName.includes('income') || groupName.includes('revenue') || ledgerName.includes('income') || ledgerName.includes('revenue') || ledgerName.includes('sales')) {
            return 'income';
        }

        // Explicit Expense check
        if (groupName.includes('expense') || ledgerName.includes('expense')) {
            return 'expense';
        }

        if (groupName.includes('vendor')) return 'vendor';
        if (groupName.includes('client') || groupName.includes('receivable')) return 'client';
        if (groupName.includes('loan') || groupName.includes('employee')) {
            return 'employee';
        }
        if (ledgerName.includes('drawing') || ledgerName.includes('withdrawal')) {
            return 'withdrawal';
        }
        if (ledgerName.includes('retained earnings') || ledgerName.includes('profit') || ledgerName.includes('loss')) {
            return 'retained_earnings';
        }
        if (groupName.includes('capital') || groupName.includes('owners') || groupName.includes('equity')) {
            return 'capital';
        }
        if (groupName.includes('fixed asset') ||
            ledgerName.includes('fixed asset') ||
            ledgerName.includes('furniture') ||
            ledgerName.includes('equipment') ||
            ledgerName.includes('electronics') ||
            ledgerName.includes('computer') ||
            ledgerName.includes('purchase')
        ) {
            return 'fixed_asset';
        }
        if (ledgerName.includes('vat') || groupName.includes('tax') || groupName.includes('vat')) {
            return 'vat_payable';
        }
        if (ledgerName.includes('salary') || ledgerName.includes('payroll') || ledgerName.includes('wages')) {
            return 'payroll';
        }
        if (groupName.includes('liabilit') || groupName.includes('payable')) {
            return 'liability';
        }
        if (groupName.includes('contra') || ledgerName.includes('transfer')) {
            return 'contra';
        }

        // Fallback: check ledger name patterns
        if (ledgerName.includes('vendor') || ledgerName.includes('supplier')) {
            return 'vendor';
        }
        if (ledgerName.includes('client') || ledgerName.includes('customer')) {
            return 'client';
        }
        if (ledgerName.includes('capital')) {
            return 'capital';
        }

        return 'other';
    };

    const fetchUsers = async () => {
        try {
            const token = JSON.parse(localStorage.getItem('user-info'))?.token;
            const origin = localStorage.getItem('user-origin');
            const response = await axios.get(`${API_ORIGIN}/api/users`, {
                headers: { Authorization: `Bearer ${token}`, 'Tenant-Origin': origin }
            });
            setUsers(response.data.users || []);
        } catch (e) { console.error('Error fetching users:', e); }
    };

    const fetchExpenseTypes = async () => {
        try {
            const token = JSON.parse(localStorage.getItem('user-info'))?.token;
            const origin = localStorage.getItem('user-origin');
            const response = await axios.get(`${API_ORIGIN}/api/expense-type`, {
                headers: { Authorization: `Bearer ${token}`, 'Tenant-Origin': origin }
            });
            setExpenseTypes(response.data.expense || []);
        } catch (e) { console.error('Error fetching expense types:', e); }
    };

    const splitOrderByRevenueType = (order) => {
        const quotation = order.quotation || order;
        const subscriptionItems = [];
        const deploymentItems = [];
        quotation?.product_item_details?.forEach(item => {
            if (item.product?.license_type?.toLowerCase() === 'service') {
                deploymentItems.push(item);
            } else {
                subscriptionItems.push(item);
            }
        });
        return {
            subscription: { items: subscriptionItems, total: quotation?.total_amount_with_subscription || 0 },
            deployment: { items: deploymentItems, total: quotation?.deplyment_amount || 0 }
        };
    };

    const handleReferenceClick = async (inv, e) => {
        if (e) e.stopPropagation();
        
        console.log('[Drilldown] Reference Clicked:', inv);
        
        // Resiliently identify the transaction data
        const mainTrx = (inv.transactions && inv.transactions.length > 0) ? inv.transactions[0] : inv;
        if (!mainTrx) {
            console.warn('[Drilldown] No transaction data found for click.');
            return;
        }

        // 1. EXHAUSTIVE ATTACHMENT SEARCH (Highest Priority)
        const attachment = inv.attachment || inv.attatchment || inv.tax_invoice_file ||
                           mainTrx.attachment || mainTrx.attatchment || mainTrx.tax_invoice_file ||
                           mainTrx.invoice?.attachment || mainTrx.invoice?.attatchment || mainTrx.invoice?.tax_invoice_file ||
                           mainTrx.expense?.attachment || mainTrx.expense?.attatchment ||
                           inv.invoice?.attachment || inv.invoice?.attatchment || inv.invoice?.tax_invoice_file;

        if (attachment) {
            console.log('[Drilldown] Attachment found, triggering preview:', attachment);
            handleDocPreview({ ...mainTrx, attachment });
            return;
        }

        console.log('[Drilldown] No direct attachment found. Falling back to type detection.');

        // 2. FALLBACK TO MODALS IF NO ATTACHMENT
        const typeStr = String(mainTrx.type || inv.type || '').toLowerCase();
        
        // Strict Type Detection
        const hasExpenseFields = !!(mainTrx.expense_id || inv.expense_id || typeStr === 'expense' || typeStr === 'bill');
        const hasInvoiceFields = !!(mainTrx.invoice_id || inv.invoice_id || typeStr === 'income' || typeStr === 'invoice' || inv.tax_invoice_id || mainTrx.tax_invoice_id || inv.tax_invoice_no);

        // PRIORITY LOGIC: Use the ledger context to decide which modal to favor if both fields exist
        let isExpense = hasExpenseFields;
        let isInvoice = hasInvoiceFields;

        if (hasExpenseFields && hasInvoiceFields) {
            if (isReceivableLedger) {
                isExpense = false; // Favor invoice in client/receivable ledgers
            } else if (isLiabilityLedger) {
                isInvoice = false; // Favor expense in vendor/liability ledgers
            }
        }

        if (isExpense) {
            const expId = mainTrx.expense_id || inv.expense_id || mainTrx.id || inv.id;
            console.log('[Drilldown] Routing to Expense Modal:', expId);
            handleExpenseClick(expId, mainTrx);
            return;
        }

        if (isInvoice) {
            // Priority 1: Direct Tax Invoice ID
            const taxInvId = inv.tax_invoice_id || mainTrx.tax_invoice_id || 
                             inv.invoice?.tax_invoices?.[0]?.id || 
                             mainTrx.invoice?.tax_invoices?.[0]?.id;

            if (taxInvId) {
                console.log('[Drilldown] Found Tax Invoice ID, routing to Tax Preview:', taxInvId);
                handleInvoiceClick(taxInvId, true); 
            } else {
                const proformaId = mainTrx.invoice_id || inv.invoice_id || mainTrx.id || inv.id;
                console.log('[Drilldown] No Tax Invoice found, routing to Proforma Preview:', proformaId);
                handleInvoiceClick(proformaId, false); 
            }
            return;
        }

        // Final Fallback
        console.warn('[Drilldown] No attachment or known type found for preview.', inv);
    };

    const handleDownloadTaxInvoicePDF = async (id, refNo) => {
        try {
            const token = JSON.parse(localStorage.getItem('user-info'))?.token;
            const origin = localStorage.getItem('user-origin');
            const response = await axios({
                url: `${API_ORIGIN}/api/taxinvoice/${id}/download`,
                method: 'GET',
                responseType: 'blob',
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Tenant-Origin': origin,
                }
            });
            const blob = new Blob([response.data], { type: 'application/pdf' });
            saveAs(blob, `tax-invoice-${refNo || id}.pdf`);
        } catch (error) {
            console.error('Error downloading tax invoice:', error);
            Swal.fire('Error', 'Failed to download tax invoice PDF', 'error');
        }
    };

    const handleInvoiceClick = async (invoiceId, isTaxInvoice = true) => {
        try {
            console.log(`[Drilldown] handleInvoiceClick for ID: ${invoiceId}, isTaxInvoice: ${isTaxInvoice}`);
            const token = JSON.parse(localStorage.getItem('user-info'))?.token;
            const origin = localStorage.getItem('user-origin');
            
            const endpoint = isTaxInvoice ? `taxinvoice/${invoiceId}` : `proformainvoice/${invoiceId}`;
            
            const response = await axios.get(`${API_ORIGIN}/api/${endpoint}`, {
                headers: { Authorization: `Bearer ${token}`, 'Tenant-Origin': origin }
            });

            // Resiliently check for invoice data
            const invoiceData = response.data?.data || response.data?.taxinvoice || response.data?.proformainvoice || response.data;
            
            if (invoiceData) {
                // AUTO-DETECT ATTACHMENT IN DETAILED DATA
                const attachment = invoiceData.attachment || 
                                   invoiceData.attatchment || 
                                   invoiceData.tax_invoice_file ||
                                   invoiceData.tax_invoice?.tax_invoice_file ||
                                   response.data?.tax_invoice?.tax_invoice_file;

                if (attachment) {
                    // Detect if this is a Tax Invoice (either directly or linked inside a Proforma)
                    const taxRecord = invoiceData.tax_invoice || response.data?.tax_invoice || (isTaxInvoice ? invoiceData : null);
                    const isTax = !!(taxRecord || invoiceData.tax_invoice_file);
                    
                    console.log('[Drilldown] Detailed invoice data has attachment, showing preview:', attachment);
                    
                    // Pass the correct ID and type for download
                    const docId = taxRecord?.id || invoiceData.id;
                    handleDocPreview({ ...invoiceData, attachment, id: docId }, isTax ? 'taxinvoice' : 'proforma');
                    return;
                }

                setSelectedInvoice(invoiceData);
                const quotation = invoiceData.proforma_invoice?.quotation || invoiceData.proformaInvoice?.quotation || invoiceData.quotation;
                if (quotation) {
                    setSelectedQuotation({ ...quotation, revenueSplit: splitOrderByRevenueType(quotation) });
                    setCustomIntegration(quotation.custom_integration || false);
                    setCustomIntegrationDescription(quotation.custom_integration_description || '');
                    try {
                        const desc = quotation.editable_deployment_descriptions;
                        setEditableDeploymentIntegration(typeof desc === 'string' ? JSON.parse(desc) : (desc || {}));
                    } catch (err) { setEditableDeploymentIntegration({}); }
                }
                setShowPreviewModal(true);
            }
        } catch (error) {
            console.error("Error fetching invoice:", error);
            Swal.fire('Error', 'Failed to fetch invoice details', 'error');
        }
    };

    const handleExpenseClick = async (expenseId, trx) => {
        try {
            console.log('[Drilldown] handleExpenseClick for ID:', expenseId);
            const token = JSON.parse(localStorage.getItem('user-info'))?.token;
            const origin = localStorage.getItem('user-origin');
            const response = await axios.get(`${API_ORIGIN}/api/expense/${expenseId}`, {
                headers: { Authorization: `Bearer ${token}`, 'Tenant-Origin': origin }
            });
            
            // Resiliently check for expense data (wrapped or flat)
            const expData = response.data?.data || response.data;

            if (expData) {
                // AUTO-DETECT ATTACHMENT IN DETAILED DATA
                const attachment = expData.attachment || expData.attatchment || expData.tax_invoice_file;
                if (attachment) {
                    console.log('[Drilldown] Detailed expense data has attachment, showing preview.');
                    handleDocPreview({ ...expData, attachment }, 'expense');
                    return;
                }

                setSelectedExpenseData(expData);
                setSelectedExpenseId(expenseId);
                setIsEditMode(false);
                setShowExpenseModal(true);
            }
        } catch (e) {
            console.error('Error fetching expense:', e);
            const attachment = trx?.attachment || trx?.attatchment || trx?.tax_invoice_file;
            if (attachment) handleDocPreview({ ...trx, attachment }, 'expense');
        }
    };

    const handleDocPreview = (entry, docType = 'expense') => {
        let rawPath = entry.attachment || 
                      entry.attatchment || 
                      entry.tax_invoice_file || 
                      entry.tax_invoice?.tax_invoice_file ||
                      entry.invoice?.attachment || 
                      entry.invoice?.attatchment || 
                      entry.invoice?.tax_invoice_file || "";
        
        if (!rawPath) {
            console.warn('[Drilldown] handleDocPreview triggered but no attachment path found.', entry);
            return;
        }

        console.log(`[Drilldown] Processing ${docType} path from backend:`, rawPath);

        let relativePath = rawPath;
        
        if (relativePath.includes('/storage/')) {
            relativePath = relativePath.split('/storage/')[1];
        } else if (relativePath.startsWith('http')) {
            try {
                const url = new URL(relativePath);
                relativePath = url.pathname.replace(/^\/storage\//, '').replace(/^\//, '');
            } catch (e) {
                console.error('[Drilldown] Invalid URL in attachment:', relativePath);
            }
        }

        relativePath = relativePath.replace(/^public\//, '').replace(/^\//, '');

        const baseUrl = (API_ORIGIN || '').replace(/\/$/, '');
        const fileUrl = `${baseUrl}/storage/${relativePath}`;
        
        // DYNAMIC DOWNLOAD ENDPOINT
        let downloadEndpoint = `${baseUrl}/api/expenses/download/${relativePath.split('/').pop()}?path=${encodeURIComponent(relativePath)}`;
        
        if (docType === 'taxinvoice') {
            downloadEndpoint = `${baseUrl}/api/taxinvoices/${entry.id}/download`;
        } else if (docType === 'proforma') {
            downloadEndpoint = `${baseUrl}/api/proformainvoice/${entry.id}/download`;
        }

        console.log('[Drilldown] Final Constructed URLs:', { preview: fileUrl, download: downloadEndpoint });
        setDocUrl(fileUrl);
        setDownloadUrl(downloadEndpoint);
        
        const displayRef = entry.invoice_no || entry.ref_no || entry.invoice?.ref_no || 'View';
        setDocTitle(`Document: ${displayRef}`);
        
        setTimeout(() => {
            console.log('[Drilldown] Opening Offcanvas');
            setShowDocOffcanvas(true);
        }, 10);
    };

    const toggleExpand = async (invId, trxList) => {
        const newExpanded = new Set(expandedInvoices);
        if (newExpanded.has(invId)) {
            newExpanded.delete(invId);
        } else {
            newExpanded.add(invId);
            // Fetch entry details for all transactions and their related journal entries
            for (const trx of trxList) {
                console.log('[Drilldown] Transaction Object:', trx);
                if (trx.entry_id && !entryDetails[trx.entry_id]) {
                    fetchWebzashEntry(trx.entry_id, { skipLoader: true });
                }

                // Fetch related Journal Entry (Accrual)
                const journalId = trx.invoice?.tax_invoices?.[0]?.journal_id || trx.invoice?.journal_id || trx.expense?.journal_id;
                if (journalId && !entryDetails[journalId]) {
                    fetchWebzashEntry(journalId, { skipLoader: true });
                }

                // Check transaction_expense_items for multiple expenses
                if (trx.transaction_expense_items?.length > 0) {
                    trx.transaction_expense_items.forEach(expItem => {
                        const expJournalId = expItem.expense?.journal_id;
                        if (expJournalId && !entryDetails[expJournalId]) {
                            fetchWebzashEntry(expJournalId, { skipLoader: true });
                        }
                    });
                }

                // NEW: Proactively fetch direct journal/entry links for total transparency
                if (trx.journal_id && !entryDetails[trx.journal_id]) {
                    fetchWebzashEntry(trx.journal_id, { skipLoader: true });
                }
                if (trx.reverse_entry_id && !entryDetails[trx.reverse_entry_id]) {
                    fetchWebzashEntry(trx.reverse_entry_id, { skipLoader: true });
                }
            }
        }
        setExpandedInvoices(newExpanded);
    };

    const fetchWebzashEntry = async (entryId, { skipLoader = false } = {}) => {
        if (!skipLoader && fetchingEntry === entryId) return;
        if (!skipLoader) setFetchingEntry(entryId);

        try {
            const webzashHeaders = { 'x-api-key': API_KEY, 'Accept': 'application/json' };
            console.log(`[Drilldown] Lazy-loading Webzash entry: ${entryId}${skipLoader ? ' (Background)' : ''}`);

            const types = ['journal', 'receipt', 'payment', 'contra'];
            let fetchedData = null;

            for (const type of types) {
                try {
                    const response = await axios.get(`${API_BASE_URL}/api/entries/view/${type}/${entryId}`, {
                        headers: webzashHeaders,
                        skipLoader: true,
                    });
                    if (response.data && (response.data.entry || response.data.Entry)) {
                        fetchedData = response.data;
                        break;
                    }
                } catch (e) {
                    continue;
                }
            }

            if (fetchedData) {
                setEntryDetails(prev => ({ ...prev, [entryId]: fetchedData }));
            }
        } catch (err) {
            console.error(`Failed to fetch entry ${entryId}:`, err);
        } finally {
            if (!skipLoader) setFetchingEntry(null);
        }
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(amount);
    };


    const getStatusBadge = (status) => {
        const s = status?.toLowerCase() || '';
        if (s.includes('paid')) return <Badge bg="success" className="rounded-1 text-uppercase" style={{ fontSize: '0.6rem' }}>Paid</Badge>;
        if (s.includes('partial')) return <Badge bg="warning" className="rounded-1 text-uppercase" style={{ fontSize: '0.6rem' }}>Partial</Badge>;
        if (s === 'journal') return <Badge bg="light" text="dark" className="border rounded-1 text-uppercase" style={{ fontSize: '0.6rem' }}>Journal</Badge>;
        return <Badge bg="danger" className="rounded-1 text-uppercase" style={{ fontSize: '0.6rem' }}>{status || 'Pending'}</Badge>;
    };

    const renderNestedDetails = (inv) => {
        const journals = [];
        const payments = [];
        const seenEntryIds = new Set();

        inv.transactions.forEach(trx => {
            // For EXPENSE records from project_expenses, show ONLY the expense journal
            if (trx.expense_id && trx.journal_id) {
                if (!seenEntryIds.has(trx.journal_id)) {
                    journals.push({
                        id: trx.journal_id,
                        type: 'Expense Journal',
                        logId: trx.external_id || trx.invoice_no || `E-${trx.expense_id}`
                    });
                    seenEntryIds.add(trx.journal_id);
                }
                // Skip all other entries for expense records
                return;
            }

            // For INVOICE records from proforma_invoices, show ONLY the invoice journal
            if (trx.invoice_id && trx.journal_id && trx.type === 'income') {
                if (!seenEntryIds.has(trx.journal_id)) {
                    journals.push({
                        id: trx.journal_id,
                        type: 'Invoice Journal',
                        logId: trx.ref_no || `INV-${trx.invoice_id}`
                    });
                    seenEntryIds.add(trx.journal_id);
                }
                // Skip all other entries for invoice records
                return;
            }

            // For CREDIT NOTE records (income_reversal)
            if (trx.journal_id && trx.type === 'income_reversal') {
                if (!seenEntryIds.has(trx.journal_id)) {
                    journals.push({
                        id: trx.journal_id,
                        type: 'Credit Note Journal',
                        logId: trx.ref_no || `CN-${trx.id}`
                    });
                    seenEntryIds.add(trx.journal_id);
                }
                return;
            }

            // For TRANSACTION records, use the original logic
            // 1. Core accounting links from transaction itself
            if (trx.journal_id && !seenEntryIds.has(trx.journal_id)) {
                journals.push({
                    id: trx.journal_id,
                    type: 'Journal',
                    logId: trx.external_id || 'JN'
                });
                seenEntryIds.add(trx.journal_id);
            }

            // 2. Accrual/Journal Entry (Standard Relation paths)
            const mainJournalId = trx.invoice?.tax_invoices?.[0]?.journal_id || trx.invoice?.journal_id || trx.expense?.journal_id;
            if (mainJournalId && !seenEntryIds.has(mainJournalId)) {
                journals.push({
                    id: mainJournalId,
                    type: 'Journal',
                    logId: trx.invoice?.ref_no || trx.ref_no || 'JN'
                });
                seenEntryIds.add(mainJournalId);
            }

            // 3. Check transaction_expense_items for expenses
            if (trx.transaction_expense_items?.length > 0) {
                trx.transaction_expense_items.forEach(expItem => {
                    const expJournalId = expItem.expense?.journal_id;
                    if (expJournalId && !seenEntryIds.has(expJournalId)) {
                        journals.push({
                            id: expJournalId,
                            type: 'Journal',
                            logId: expItem.expense?.external_id || 'JN'
                        });
                        seenEntryIds.add(expJournalId);
                    }
                });
            }

            // 4. Financial Transaction Entry
            if (trx.entry_id && !seenEntryIds.has(trx.entry_id)) {
                payments.push({
                    id: trx.entry_id,
                    type: trx.type || 'Payment',
                    logId: trx.external_id || '0'
                });
                seenEntryIds.add(trx.entry_id);
            }

            // 5. Reversal entries
            if (trx.reverse_entry_id && !seenEntryIds.has(trx.reverse_entry_id)) {
                payments.push({
                    id: trx.reverse_entry_id,
                    type: 'Reversal',
                    logId: trx.external_id || 'REV'
                });
                seenEntryIds.add(trx.reverse_entry_id);
            }
        });

        const renderEntry = (entry, eIdx) => {
            const webzashData = entryDetails[entry.id];
            return (
                <div key={eIdx} className="mb-3 bg-white rounded-3 shadow-sm border-0 overflow-hidden compact-journal-card animate-slide-up" style={{ animationDelay: `${eIdx * 0.05}s` }}>
                    <div className="px-3 py-2 bg-light bg-opacity-40 border-bottom d-flex justify-content-between align-items-center">
                        <div className="d-flex align-items-center gap-3">
                            <h2 className="mb-0 fw-bold text-dark h3">{ledger?.name || 'Ledger Details'}</h2>
                            {ledgerDebug && (
                                <Badge bg={ledgerDebug.api?.success ? "info" : "danger"} className="text-uppercase" style={{ fontSize: '0.6rem' }}>
                                    Debug Trace: {ledgerDebug.type} | ID: {ledgerDebug.ledger_id} | API: {ledgerDebug.api?.status || 'N/A'} | P: {JSON.stringify(ledgerDebug.api?.params || {})} | RAW: {ledgerDebug.api?.raw_peek || 'EMPTY'}
                                </Badge>
                            )}
                            <Badge bg={entry.type ? (entry.type.toLowerCase().includes('journal') ? "info" : "secondary") : "secondary"} className="text-uppercase ls-1 px-2 py-1" style={{ fontSize: '0.6rem', fontWeight: '800' }}>
                                {entry.type}
                            </Badge>
                            <span className="text-muted fw-bold" style={{ fontSize: '0.6rem' }}>REF: <span className="text-dark">#{entry.logId}</span></span>
                        </div>
                    </div>

                    {fetchingEntry === entry.id ? (
                        <div className="text-center py-4 bg-white"><Spinner animation="border" size="sm" variant="primary" /></div>
                    ) : webzashData ? (
                        <div className="bg-white">
                            <div className="d-flex flex-column py-2">
                                {(webzashData.entryitems || webzashData.Entryitem || []).map((item, i) => (
                                    <div key={i} className={`d-flex align-items-center px-4 py-2 my-1 mx-2 rounded-3 ${item.ledger_name === ledger?.name ? 'target-ledger-row' : 'ledger-row-clean'}`}>
                                        <div className="flex-grow-1">
                                            <div className={`fw-bold ${item.ledger_name === ledger?.name ? 'text-primary' : 'text-dark'}`} style={{ fontSize: '0.85rem' }}>
                                                {item.ledger_name || item.Ledger?.name}
                                                {item.ledger_name === ledger?.name && <Badge bg="primary" className="ms-2 px-1 py-0 rounded-pill" style={{ fontSize: '10px' }}>Target</Badge>}
                                            </div>
                                        </div>
                                        <div className="text-end d-flex gap-4" style={{ width: '280px' }}>
                                            <div className={`amount-badge text-end flex-grow-1 ${item.dc === 'D' ? 'text-danger' : 'text-lighter'}`} style={{ width: '130px', fontSize: '0.85rem' }}>
                                                {item.dc === 'D' ? formatCurrency(item.amount || item.dr_amount) : '—'}
                                            </div>
                                            <div className={`amount-badge text-end flex-grow-1 ${item.dc === 'C' ? 'text-success' : 'text-lighter'}`} style={{ width: '130px', fontSize: '0.85rem' }}>
                                                {item.dc === 'C' ? formatCurrency(item.amount || item.cr_amount) : '—'}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <div className="px-4 py-2 mt-1 bg-light bg-opacity-10 border-top d-flex align-items-center">
                                <small className="text-muted text-uppercase fw-bold ls-1 me-3" style={{ fontSize: '0.6rem' }}>Narrative</small>
                                <p className="mb-0 text-dark fw-medium" style={{ fontSize: '0.75rem' }}>{(webzashData.entry || webzashData.Entry)?.narration || 'No narrative provided.'}</p>
                            </div>
                        </div>
                    ) : (
                        <div className="p-4 text-center text-muted x-small fst-italic">Waiting for journal synchronization...</div>
                    )}
                </div>
            );
        };

        return (
            <Tabs defaultActiveKey={journals.length > 0 ? "journal" : "payments"} className="custom-ledger-tabs mb-4 px-2 border-0">
                <Tab eventKey="journal" title={<span className="d-flex align-items-center"><FiFileText className="me-2" /> Journal</span>} disabled={journals.length === 0}>
                    <div className="pt-3">
                        {journals.length > 0 ? journals.map((entry, idx) => renderEntry(entry, idx)) : (
                            <div className="text-center py-4 bg-white rounded-3 border dashed muted small">No accrual journal linked to this transaction Group.</div>
                        )}
                    </div>
                </Tab>
                <Tab eventKey="payments" title={<span className="d-flex align-items-center"><FiCheckCircle className="me-2" /> Financial Transactions</span>} disabled={payments.length === 0}>
                    <div className="pt-3">
                        {payments.length > 0 ? payments.map((entry, idx) => renderEntry(entry, idx)) : (
                            <div className="text-center py-4 bg-white rounded-3 border dashed muted small">No direct payment/receipt entries found.</div>
                        )}
                    </div>
                </Tab>
            </Tabs>
        );
    };

    const renderWebzashStatement = () => {
        if (loadingStatement) return <div className="text-center py-5"><Spinner animation="border" variant="primary" /></div>;
        if (!webzashStatement || !webzashStatement.entries || webzashStatement.entries.length === 0) {
            return (
                <div className="text-center py-5 opacity-50">
                    <FiFileText className="fs-1 mb-3" />
                    <p>No bookkeeping entries found in Webzash for this period.</p>
                </div>
            );
        }

        return (
            <Card className="border-0 shadow-sm overflow-hidden rounded-4">
                <div className="table-responsive">
                    <Table hover className="mb-0 align-middle ledger-table">
                        <thead className="bg-light border-bottom">
                            <tr>
                                <th className="ps-4 py-3 border-0 text-muted small text-uppercase ls-1">Date</th>
                                <th className="py-3 border-0 text-muted small text-uppercase ls-1">Narration</th>
                                <th className="py-3 border-0 text-muted small text-uppercase ls-1 text-end">Debit</th>
                                <th className="py-3 border-0 text-muted small text-uppercase ls-1 text-end">Credit</th>
                                <th className="pe-4 py-3 border-0 text-end text-muted small text-uppercase ls-1">Ref</th>
                            </tr>
                        </thead>
                        <tbody>
                            {webzashStatement.entries.map((entry, idx) => {
                                // Extract the core entry data
                                const ent = entry.Entry || {};
                                const item = entry.Entryitem || {};

                                return (
                                    <tr key={idx} className="ledger-row">
                                        <td className="ps-4">
                                            <div className="fw-medium" style={{ fontSize: '0.85rem' }}>{dayjs(ent.date).format('DD MMM YYYY')}</div>
                                        </td>
                                        <td className="py-3">
                                            <div className="text-dark fw-medium mb-1" style={{ fontSize: '0.85rem', maxWidth: '350px' }}>{ent.narration}</div>
                                            <Badge bg="light" text="dark" className="border rounded-pill x-small text-uppercase ls-1" style={{ fontWeight: 800 }}>Ref: #{ent.number}</Badge>
                                        </td>
                                        <td className="text-end">
                                            {item.dc === 'D' ? <span className="text-danger fw-bold">{formatCurrency(item.amount)}</span> : '—'}
                                        </td>
                                        <td className="text-end">
                                            {item.dc === 'C' ? <span className="text-success fw-bold">{formatCurrency(item.amount)}</span> : '—'}
                                        </td>
                                        <td className="pe-4 text-end">
                                            <span className="text-muted x-small">#{ent.id}</span>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </Table>
                </div>
            </Card>
        );
    };

    const determineDC = (trx) => {
        if (trx.dc) return trx.dc === 'D' ? 'DR' : 'CR';
        const lType = determineLedgerType(resolvedLedger || ledger);

        // Liability/Equity/Income types: Expenses increase (CR), Payments decrease (DR)
        if (['liability', 'vendor', 'payroll', 'vat_payable', 'capital', 'retained_earnings'].includes(lType)) {
            if (trx.type === 'advance') {
                return (trx.advance_type === 'vendor') ? 'DR' : 'CR'; // Advance to vendor is DR (reduces liability)
            }
            if (['expense', 'payroll', 'salary', 'vat_payable'].includes(trx.type)) return 'CR';
            if (['payment', 'receipt'].includes(trx.type)) return 'DR';
        }

        // Asset/Expense types: Invoices increase (DR), Receipts decrease (CR)
        if (['client', 'bank', 'cash', 'fixed_asset', 'employee'].includes(lType)) {
            if (trx.type === 'advance') {
                return (trx.advance_type === 'client' || !trx.advance_type) ? 'CR' : 'DR'; // Advance from client is CR (reduces receivable)
            }
            if (['income', 'invoice'].includes(trx.type)) return 'DR';
            if (['payment', 'receipt', 'expense'].includes(trx.type)) return 'CR';
        }
        return '';
    };

    // Calculate balance from the list whenever invoices change
    useEffect(() => {
        if (!invoices || invoices.length === 0) {
            setCalculatedBalance(0);
            return;
        }

        const lType = determineLedgerType(resolvedLedger || ledger);
        // Liability/Receivable: Balance = Amount - Paid (Outstanding)
        const isOutstandingType = ['liability', 'vendor', 'sundry_creditors', 'employee', 'sundry_debtors', 'client', 'payroll', 'vat_payable'].includes(lType) || (resolvedLedger || ledger).name.toLowerCase().includes('payable') || (resolvedLedger || ledger).name.toLowerCase().includes('receivable');

        // Asset/Expense: Balance = Sum of Amounts (Accumulation)
        // Fixed Asset, Expense, Bank, Cash, Capital (usually just sum of txns)
        // For simple drilldown list, just sum the display amounts or base amounts.

        let total = 0;

        if (isOutstandingType) {
            total = invoices.reduce((sum, item) => {
                const firstTrx = item.transactions?.[0];
                if (!firstTrx) return sum;
                // Use pending_balance or calculate it.
                // Note: paid_amount might be global total paid. 
                // If we want "Balance existing AT THAT DATE", we strictly need date-based payments.
                // But list usually implies current state of those specific bills.
                // Ideally this sums the "Remaining Balance" of the bills in the list.
                const amount = parseFloat(firstTrx.pending_balance ?? (firstTrx.amount - (firstTrx.paid_amount || 0)));
                return sum + (isNaN(amount) ? 0 : amount);
            }, 0);
        } else if (!['bank', 'cash', 'fixed_asset', 'capital', 'other'].includes(lType)) {
            // For Fixed Assets / Expenses: Simply sum the Amounts.
            total = invoices.reduce((sum, item) => {
                const firstTrx = item.transactions?.[0];
                if (!firstTrx) return sum;
                const amount = parseFloat(firstTrx.amount || 0); // Gross Amount
                return sum + (isNaN(amount) ? 0 : amount);
            }, 0);
        } else {

            // Force fallback to Webzash statement closing balance
            total = null;
        }

        setCalculatedBalance(total);
    }, [invoices, resolvedLedger, ledger]);

    const [activeBreakdownTab, setActiveBreakdownTab] = useState('closing');

    // Calculate totals for summary cards from breakdownData
    const summaryTotals = useMemo(() => {
        const opening = (breakdownData.opening || []).reduce((sum, item) => sum + (item.balance || 0), 0);
        const debits = (breakdownData.debits || []).reduce((sum, item) => sum + (item.total_amount || 0), 0);
        const credits = (breakdownData.credits || []).reduce((sum, item) => sum + (item.total_amount || 0), 0);
        
        // Final outstanding from the closing list
        const closing = (breakdownData.closing || []).reduce((sum, item) => sum + (item.balance || 0), 0);
        
        return { opening, debits, credits, closing };
    }, [breakdownData]);

    return (
        <>
            <Offcanvas show={show && !showExpenseModal && !showPreviewModal && !showDocOffcanvas} onHide={onHide} placement="end" style={{ width: '950px' }} className="ledger-details-offcanvas shadow-lg">
                <Offcanvas.Header closeButton className="border-bottom sticky-top py-4 px-4 header-gradient">
                {/* ... Header Content ... */}
                <Offcanvas.Title className="w-100">
                    <div className="d-flex align-items-center justify-content-between">
                        <div className="d-flex align-items-center">
                            <div className="bg-white bg-opacity-20 p-2 rounded-4 me-3 d-flex align-items-center justify-content-center shadow-sm backdrop-blur" style={{ width: '50px', height: '50px' }}>
                                <FiInfo className="text-black fs-3" />
                            </div>
                            <div>
                                <div className="fs-4 fw-bold text-black mb-0 tracking-tight">{ledger?.name}</div>
                                <div className="d-flex align-items-center text-black text-opacity-75" style={{ fontSize: '0.8rem', marginTop: '2px' }}>
                                    <FiExternalLink className="me-1" size={12} />
                                    <span>Detailed Ledger Analysis & Reconciliation</span>
                                </div>
                            </div>
                        </div>
                        <div className="d-none d-md-block pe-5">
                            <Badge bg="white" text="primary" className="rounded-pill px-3 py-2 shadow-sm border-0">
                                <FiHash className="me-1" /> Ledger View
                            </Badge>
                        </div>
                    </div>
                </Offcanvas.Title>
            </Offcanvas.Header>
            <Offcanvas.Body className="p-0 bg-soft-blue">
                <div className="p-4 bg-white border-bottom shadow-sm mb-4">
                    <Row className="g-3">
                        <Col md={12}>
                            <div className="d-flex gap-3 mb-3">
                                <div 
                                    className={`p-3 border rounded-3 bg-white flex-grow-1 shadow-sm position-relative overflow-hidden cursor-pointer transition-all ${activeBreakdownTab === 'opening' ? 'border-primary border-2 shadow' : ''}`}
                                    onClick={() => setActiveBreakdownTab('opening')}
                                >
                                    <div className={`position-absolute top-0 start-0 h-100 ${
                                        (webzashStatement?.opening_balance?.dc === 'D') 
                                            ? (isLiabilityLedger ? 'bg-success' : 'bg-primary') 
                                            : (isLiabilityLedger ? 'bg-danger' : 'bg-success')
                                    }`} style={{ width: '4px', opacity: 0.5 }}></div>
                                    <small className="text-muted d-block mb-1 text-uppercase fw-bold ls-1" style={{ fontSize: '0.6rem' }}>Opening Balance</small>
                                    <div className="fw-bold h5 mb-0">
                                        {formatCurrency(
                                            drilldownData?.official_totals?.opening ?? (
                                                (!projectId && webzashStatement)
                                                    ? (webzashStatement?.data?.opening_balance?.amount ?? webzashStatement?.opening_balance?.amount ?? webzashStatement?.data?.opening_balance ?? webzashStatement?.opening_balance ?? 0)
                                                    : (summaryTotals.opening || (webzashStatement?.data?.opening_balance?.amount ?? webzashStatement?.opening_balance?.amount ?? 0))
                                            )
                                        )} 
                                        <span className="small opacity-50 ms-1">
                                            {(drilldownData?.official_totals?.opening_dc || webzashStatement?.data?.opening_balance?.dc || webzashStatement?.opening_balance?.dc || webzashStatement?.data?.opening_dc || webzashStatement?.opening_dc) || (isLiabilityLedger ? 'C' : 'D')}r
                                        </span>
                                    </div>
                                </div>
                                <div 
                                    className={`p-3 border rounded-3 bg-white flex-grow-1 shadow-sm position-relative overflow-hidden cursor-pointer transition-all ${activeBreakdownTab === 'debit' ? 'border-danger border-2 shadow' : ''}`}
                                    onClick={() => setActiveBreakdownTab('debit')}
                                >
                                    <div className="position-absolute top-0 start-0 h-100 bg-danger" style={{ width: '4px', opacity: 0.2 }}></div>
                                    <small className="text-muted d-block mb-1 text-uppercase fw-bold ls-1" style={{ fontSize: '0.6rem' }}>Total Debit</small>
                                    <div className="fw-bold h5 mb-0 text-danger">
                                        {formatCurrency(
                                            drilldownData?.official_totals?.debits ?? (
                                                (!projectId && webzashStatement)
                                                    ? (webzashStatement?.data?.period_totals?.dr_total || webzashStatement?.period_totals?.dr_total || webzashStatement?.data?.total_debit || webzashStatement?.total_debit || webzashStatement?.data?.total_dr || webzashStatement?.total_dr || 0)
                                                    : (summaryTotals.debits || (webzashStatement?.data?.total_debit ?? webzashStatement?.total_debit ?? 0))
                                            )
                                        )}
                                    </div>
                                </div>
                                <div 
                                    className={`p-3 border rounded-3 bg-white flex-grow-1 shadow-sm position-relative overflow-hidden cursor-pointer transition-all ${activeBreakdownTab === 'credit' ? 'border-success border-2 shadow' : ''}`}
                                    onClick={() => setActiveBreakdownTab('credit')}
                                >
                                    <div className="position-absolute top-0 start-0 h-100 bg-success" style={{ width: '4px', opacity: 0.2 }}></div>
                                    <small className="text-muted d-block mb-1 text-uppercase fw-bold ls-1" style={{ fontSize: '0.6rem' }}>Total Credit</small>
                                    <div className="fw-bold h5 mb-0 text-success">
                                        {formatCurrency(
                                            drilldownData?.official_totals?.credits ?? (
                                                (!projectId && webzashStatement)
                                                    ? (webzashStatement?.data?.period_totals?.cr_total || webzashStatement?.period_totals?.cr_total || webzashStatement?.data?.total_credit || webzashStatement?.total_credit || webzashStatement?.data?.total_cr || webzashStatement?.total_cr || 0)
                                                    : (summaryTotals.credits || (webzashStatement?.data?.total_credit ?? webzashStatement?.total_credit ?? 0))
                                            )
                                        )}
                                    </div>
                                </div>
                                <div 
                                    className={`p-3 border rounded-3 bg-white bg-opacity-10 flex-grow-1 shadow-sm position-relative overflow-hidden cursor-pointer transition-all ${activeBreakdownTab === 'closing' ? 'border-primary border-2 shadow' : ''}`}
                                    onClick={() => setActiveBreakdownTab('closing')}
                                    style={{ borderLeft: '4px solid #0d6efd !important' }}
                                >
                                    <small className="text-muted d-block mb-1 text-uppercase fw-bold ls-1" style={{ fontSize: '0.6rem' }}>Closing Balance</small>
                                    <div className="fw-bold h4 mb-0 text-primary">
                                        {formatCurrency(
                                            drilldownData?.official_totals?.closing ?? (
                                                (!projectId && webzashStatement)
                                                    ? (webzashStatement?.data?.closing_balance?.amount ?? webzashStatement?.closing_balance?.amount ?? webzashStatement?.data?.closing_balance ?? webzashStatement?.closing_balance ?? 0)
                                                    : (summaryTotals.closing || calculatedBalance || (webzashStatement?.data?.closing_balance?.amount ?? webzashStatement?.closing_balance?.amount ?? 0))
                                            )
                                        )}
                                        <Badge bg={(drilldownData?.official_totals?.closing_dc || webzashStatement?.data?.closing_balance?.dc || webzashStatement?.closing_balance?.dc || webzashStatement?.data?.closing_dc || webzashStatement?.closing_dc || (resolvedLedger || ledger)?.dc || 'D') === 'D' 
                                            ? (isLiabilityLedger ? 'success' : 'danger') 
                                            : (isLiabilityLedger ? 'danger' : 'success')} 
                                            className="ms-2 px-2 py-1" style={{ fontSize: '0.6rem' }}>
                                            {(drilldownData?.official_totals?.closing_dc || webzashStatement?.data?.closing_balance?.dc || webzashStatement?.closing_balance?.dc || webzashStatement?.data?.closing_dc || webzashStatement?.closing_dc || (resolvedLedger || ledger)?.dc || 'D') === 'D' ? 'DEBIT' : 'CREDIT'}
                                        </Badge>
                                    </div>
                                </div>
                            </div>
                        </Col>
                        <Col md={12}>
                            <div className="p-2 border rounded-3 bg-light d-flex align-items-center justify-content-center gap-4">
                                <div className="d-flex align-items-center gap-2">
                                    <FiCalendar className="text-muted" />
                                    <span className="small fw-bold text-dark">
                                        {startDate ? dayjs(startDate).format('MMM D, YYYY') : 'Earliest'}
                                        <FiChevronRight size={10} className="mx-1" />
                                        {endDate ? dayjs(endDate).format('MMM D, YYYY') : 'Latest'}
                                    </span>
                                </div>
                                <div className="border-start h-100 mx-2" style={{ width: '1px', height: '20px', backgroundColor: '#e2e8f0' }}></div>
                            </div>
                        </Col>
                        {/* No toggle anymore */}
                    </Row>
                </div>

                <div className="px-4 pb-5">
                    <Tabs
                        activeKey={activeTab}
                        onSelect={(k) => setActiveTab(k)}
                        className="custom-main-tabs mb-4 px-2"
                    >
                        <Tab
                            eventKey="summary"
                            title={
                                <span className="d-flex align-items-center">
                                    <FiBriefcase className="me-2" />
                                    Transaction Summary
                                    {invoices.length > 0 && <Badge bg="primary" pill className="ms-2 small">{invoices.length}</Badge>}
                                </span>
                            }
                        >
                            <div className="pt-3">
                                {loading ? (
                                    <div className="text-center py-5">
                                        <Spinner animation="border" variant="primary" size="lg" className="mb-3" />
                                        <h6 className="text-muted fw-normal">Analyzing transaction patterns...</h6>
                                    </div>
                                ) : error ? (
                                    <Alert variant="danger" className="border-0 shadow-sm rounded-3 p-4">
                                        <div className="d-flex align-items-center">
                                            <FiInfo className="me-3 fs-3" />
                                            <div><strong>Error:</strong> {error}</div>
                                        </div>
                                    </Alert>
                                ) : (invoices.length === 0 && 
                                    breakdownData.opening.length === 0 && 
                                    breakdownData.debits.length === 0 && 
                                    breakdownData.credits.length === 0) ? (
                                    <div className="text-center py-5 px-3 bg-white border dashed rounded-4 shadow-sm opacity-75">
                                        <FiBriefcase className="fs-1 mb-3 text-muted opacity-25" />
                                        <h5 className="text-dark fw-bold">No Transactions</h5>
                                    </div>
                                ) : (
                                    <div className="ledger-card-container">
                                        {/* New Breakdown View for Receivables/Payables/Advances */}
                                        {(isLiabilityLedger || isReceivableLedger || determineLedgerType(resolvedLedger || ledger) === 'advance') && (breakdownData.opening.length > 0 || breakdownData.debits.length > 0 || breakdownData.credits.length > 0 || breakdownData.closing.length > 0) ? (
                                            <div className="breakdown-sections">
                                                {/* Opening Balance Section */}
                                                {(activeBreakdownTab === 'all' || activeBreakdownTab === 'opening') && (
                                                    <div className="mb-4">
                                                        <div className="d-flex align-items-center mb-3 bg-light p-2 rounded-3">
                                                            <Badge bg="secondary" className="me-2 px-3 py-2">
                                                                {isLiabilityLedger ? 'OPENING UNPAID BILLS' : (isReceivableLedger ? 'OPENING UNPAID INVOICES' : 'OPENING UNPAID ADVANCES')}
                                                            </Badge>
                                                            <h6 className="mb-0 fw-bold">
                                                                {isLiabilityLedger ? 'Unpaid bills from previous period' : (isReceivableLedger ? 'Unpaid invoices from previous year' : 'Outstanding advances from previous period')}
                                                            </h6>
                                                        </div>
                                                        <Table responsive hover className="ledger-table border rounded-3 bg-white">
                                                            <thead className="bg-light">
                                                                <tr>
                                                                    <th>Date</th>
                                                                    <th>Ref No</th>
                                                                    <th>{determineLedgerType(resolvedLedger || ledger) === 'advance' ? 'Entity (Client/Vendor/User)' : 'Project'}</th>
                                                                    <th className="text-end">Total Amount</th>
                                                                    <th className="text-end">Paid Amount</th>
                                                                    <th className="text-end">Balance</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {breakdownData.opening.map(item => (
                                                                    <tr key={item.id} className="small">
                                                                        <td>{dayjs(item.date).format('DD MMM YY')}</td>
                                                                        <td className="fw-bold text-primary" onClick={(e) => handleReferenceClick(item, e)} style={{ cursor: 'pointer', textDecoration: 'underline' }}>{item.ref_no}</td>
                                                                        <td>{item.project_name}</td>
                                                                        <td className="text-end fw-bold">{formatCurrency(item.total_amount)}</td>
                                                                        <td className="text-end text-success fw-bold">{formatCurrency(item.paid_amount)}</td>
                                                                        <td className="text-end text-danger">{formatCurrency(item.balance)}</td>
                                                                    </tr>
                                                                ))}
                                                                {breakdownData.opening.length === 0 && (
                                                                    <tr><td colSpan="6" className="text-center py-4 text-muted">No opening items found for this filter.</td></tr>
                                                                )}
                                                            </tbody>
                                                        </Table>
                                                    </div>
                                                )}

                                                {/* Debits Section */}
                                                {(activeBreakdownTab === 'all' || activeBreakdownTab === 'debit') && (
                                                    <div className="mb-4">
                                                        <div className="d-flex align-items-center mb-3 bg-danger bg-opacity-10 p-2 rounded-3 border-start border-danger border-4">
                                                            <Badge bg="danger" className="me-2 px-3 py-2">
                                                                {isLiabilityLedger ? 'DEBIT / PAYMENTS MADE' : (isReceivableLedger ? 'DEBIT / NEW INVOICES' : 'DEBIT MOVEMENTS')}
                                                            </Badge>
                                                            <h6 className="mb-0 fw-bold">
                                                                {isLiabilityLedger ? 'Payments made to vendors' : (isReceivableLedger ? 'New invoices issued to clients' : 'Debit transactions in this period')}
                                                            </h6>
                                                        </div>
                                                        <Table responsive hover className="ledger-table border rounded-3 bg-white shadow-sm">
                                                            <thead className="bg-light">
                                                                <tr>
                                                                    <th>Date</th>
                                                                    <th>Ref No</th>
                                                                    <th>{determineLedgerType(resolvedLedger || ledger) === 'advance' ? 'Entity (Client/Vendor/User)' : 'Project'}</th>
                                                                    <th className="text-end">Total Amount</th>
                                                                    <th className="text-end">Paid Amount</th>
                                                                    <th className="text-end">Balance</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {breakdownData.debits.map(item => (
                                                                    <tr key={item.id} className="small">
                                                                        <td>{dayjs(item.date).format('DD MMM YY')}</td>
                                                                        <td className="fw-bold text-primary" onClick={(e) => handleReferenceClick(item, e)} style={{ cursor: 'pointer', textDecoration: 'underline' }}>{item.ref_no}</td>
                                                                        <td>{item.project_name}</td>
                                                                        <td className="text-end fw-bold">{formatCurrency(item.total_amount)}</td>
                                                                        <td className="text-end text-success fw-bold">{formatCurrency(item.paid_amount)}</td>
                                                                        <td className="text-end text-danger">{formatCurrency(item.balance)}</td>
                                                                    </tr>
                                                                ))}
                                                                {breakdownData.debits.length === 0 && (
                                                                    <tr><td colSpan="6" className="text-center py-4 text-muted">No debit transactions found for this period.</td></tr>
                                                                )}
                                                            </tbody>
                                                        </Table>
                                                    </div>
                                                )}

                                                {/* Credits Section */}
                                                {(activeBreakdownTab === 'all' || activeBreakdownTab === 'credit') && (
                                                    <div className="mb-4">
                                                        <div className="d-flex align-items-center mb-3 bg-success bg-opacity-10 p-2 rounded-3 border-start border-success border-4">
                                                            <Badge bg="success" className="me-2 px-3 py-2">
                                                                {isLiabilityLedger ? 'CREDIT / NEW BILLS' : (isReceivableLedger ? 'CREDIT / PAYMENTS RECEIVED' : 'CREDIT MOVEMENTS')}
                                                            </Badge>
                                                            <h6 className="mb-0 fw-bold">
                                                                {isLiabilityLedger ? 'New expenses and bills received' : (isReceivableLedger ? 'Payments received for invoices' : 'Credit transactions in this period')}
                                                            </h6>
                                                        </div>
                                                        <Table responsive hover className="ledger-table border rounded-3 bg-white">
                                                            <thead className="bg-light">
                                                                <tr>
                                                                    <th>Date</th>
                                                                    <th>Ref No</th>
                                                                    <th>{determineLedgerType(resolvedLedger || ledger) === 'advance' ? 'Entity (Client/Vendor/User)' : 'Project'}</th>
                                                                    <th className="text-end">Total Amount</th>
                                                                    <th className="text-end">Paid Amount</th>
                                                                    <th className="text-end">Balance</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {breakdownData.credits.map(item => (
                                                                    <tr key={item.id} className="small">
                                                                        <td>{dayjs(item.date).format('DD MMM YY')}</td>
                                                                        <td className="fw-bold text-primary" onClick={(e) => handleReferenceClick(item, e)} style={{ cursor: 'pointer', textDecoration: 'underline' }}>{item.ref_no}</td>
                                                                        <td>{item.project_name}</td>
                                                                        <td className="text-end fw-bold">{formatCurrency(item.total_amount)}</td>
                                                                        <td className="text-end text-success fw-bold">{formatCurrency(item.paid_amount)}</td>
                                                                        <td className="text-end text-danger">{formatCurrency(item.balance)}</td>
                                                                    </tr>
                                                                ))}
                                                                {breakdownData.credits.length === 0 && (
                                                                    <tr><td colSpan="6" className="text-center py-4 text-muted">No credit transactions found for this period.</td></tr>
                                                                )}
                                                            </tbody>
                                                        </Table>
                                                    </div>
                                                )}

                                                {/* Closing Section */}
                                                {(activeBreakdownTab === 'all' || activeBreakdownTab === 'closing') && (
                                                    <div className="mb-4">
                                                        <div className="d-flex align-items-center mb-3 bg-light bg-opacity-10 p-2 rounded-3 border-start border-primary border-4">
                                                            <Badge bg="light" className="me-2 px-3 py-2">
                                                                {isLiabilityLedger ? 'OUTSTANDING BILLS' : (isReceivableLedger ? 'FINAL OUTSTANDING' : 'OUTSTANDING ADVANCES')}
                                                            </Badge>
                                                            <h6 className="mb-0 fw-bold">Items contributing to current balance</h6>
                                                        </div>
                                                        <Table responsive hover className="ledger-table border rounded-3 bg-white shadow-sm">
                                                            <thead className="bg-dark text-white">
                                                                <tr>
                                                                    <th>Date</th>
                                                                    <th>Ref No</th>
                                                                    <th>{determineLedgerType(resolvedLedger || ledger) === 'advance' ? 'Entity (Client/Vendor/User)' : 'Project'}</th>
                                                                    <th className="text-end">Total Amount</th>
                                                                    <th className="text-end">Paid Amount</th>
                                                                    <th className="text-end">Balance</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {breakdownData.closing.map(item => (
                                                                    <tr key={item.id} className="small">
                                                                        <td>{dayjs(item.date).format('DD MMM YY')}</td>
                                                                        <td className="fw-bold text-primary" onClick={(e) => handleReferenceClick(item, e)} style={{ cursor: 'pointer', textDecoration: 'underline' }}>{item.ref_no}</td>
                                                                        <td>{item.project_name}</td>
                                                                        <td className="text-end fw-bold">{formatCurrency(item.total_amount)}</td>
                                                                        <td className="text-end text-success">{formatCurrency(item.paid_amount)}</td>
                                                                        <td className="text-end text-danger fw-bold">{formatCurrency(item.balance)}</td>
                                                                    </tr>
                                                                ))}
                                                                {breakdownData.closing.length === 0 && (
                                                                    <tr><td colSpan="6" className="text-center py-4 text-muted">No outstanding items found for this balance.</td></tr>
                                                                )}
                                                            </tbody>
                                                        </Table>
                                                    </div>
                                                )}
                                            </div>
                                        ) : (
                                            /* Original Table View for other ledgers */
                                            <Card className="border-0 shadow-sm overflow-hidden rounded-4">
                                                <div className="table-responsive">
                                                    <Table hover className="mb-0 align-middle ledger-table">
                                                        <thead className="bg-light border-bottom">
                                                            <tr>
                                                                <th className="ps-4 py-3 border-0 text-muted small text-uppercase ls-1">Date</th>
                                                                <th className="py-3 border-0 text-muted small text-uppercase ls-1" style={{ width: '180px' }}>Reference No</th>
                                                                <th className="py-3 border-0 text-muted small text-uppercase ls-1">Project</th>
                                                                <th className="py-3 border-0 text-end text-muted small text-uppercase ls-1">Amount (AED)</th>
                                                                <th className="pe-4 py-3 border-0" style={{ width: '50px' }}></th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {invoices.map((inv) => {
                                                                const mainTrx = inv.transactions?.[0] || {};
                                                                const dc = determineDC(mainTrx);
                                                                const isPending = pendingOnly;

                                                                // For expense transactions, prioritize Net Amount (amount) for the main column
                                                                const isExpenseType = ['expense', 'payroll', 'salary', 'vat_payable', 'income_reversal'].includes(mainTrx.type);
                                                                const baseAmount = isExpenseType && mainTrx.amount !== undefined
                                                                    ? mainTrx.amount
                                                                    : inv.display_amount;

                                                                // Logic Update:
                                                                // If we have a startDate (meaning it's a historical/period view), we want to see the Original Amount (baseAmount).
                                                                // We only show pending_balance if we are practically in "Pending Only" mode without date overrides.
                                                                // Also, if the item is fully paid, pending_balance is 0, which looks wrong in a history list.
                                                                const showPendingBalance = isPending && !startDate && mainTrx.pending_balance !== undefined;

                                                                const displayAmt = showPendingBalance
                                                                    ? mainTrx.pending_balance
                                                                    : baseAmount;

                                                                const paidAmount = mainTrx.paid_amount || 0;
                                                                const isPartiallyPaid = paidAmount > 0 && paidAmount < baseAmount;

                                                                return (
                                                                    <React.Fragment key={inv.id}>
                                                                        <tr
                                                                            className={`ledger-row cursor-pointer transition-all ${expandedInvoices.has(inv.id) ? 'expanded-row' : ''}`}
                                                                            onClick={() => toggleExpand(inv.id, inv.transactions)}
                                                                        >
                                                                            <td className="ps-4 timeline-col">
                                                                                <div className="timeline-point"></div>
                                                                                <div className="fw-mute date-primary" style={{ fontSize: '0.9rem' }}>{dayjs(inv.date).format('DD MMM YYYY')}</div>
                                                                            </td>
                                                                            <td className="py-4">
                                                                                <div
                                                                                    className="text-primary-hover mb-0 record-ref fw-bold"
                                                                                    style={{ fontSize: '0.85rem', cursor: 'pointer', textDecoration: 'underline', textUnderlineOffset: '3px', textDecorationColor: 'rgba(59, 130, 246, 0.3)' }}
                                                                                    onClick={(e) => handleReferenceClick(inv, e)}
                                                                                >
                                                                                    <FiExternalLink className="me-1 small opacity-50" />
                                                                                    {inv.tax_invoice_no || inv.invoice?.tax_invoices?.[0]?.invoice_no || inv.invoice?.tax_invoices?.[0]?.ref_no || inv.ref_no}
                                                                                </div>
                                                                            </td>
                                                                            <td className="py-4">
                                                                                <div className="text-secondary fw-medium" style={{ fontSize: '0.85rem' }}>{inv.project_name}</div>
                                                                            </td>
                                                                            <td className="text-end py-4">
                                                                                <div className="d-flex align-items-center justify-content-end gap-2">
                                                                                    <div className="fw-mute amount-text" style={{ fontSize: '0.95rem' }}>{formatCurrency(displayAmt)}</div>
                                                                                    {dc && <span className={`badge x-small border rounded-1 p-1 ${dc === 'CR' ? 'bg-success bg-opacity-10 text-success' : 'bg-danger bg-opacity-10 text-danger'}`} style={{ fontSize: '0.65rem', minWidth: '25px' }}>{dc}</span>}
                                                                                </div>
                                                                                {isPending && isPartiallyPaid && (
                                                                                    <div className="text-muted x-small mt-1">
                                                                                        Paid: {formatCurrency(paidAmount)} | Pending: {formatCurrency(displayAmt)}
                                                                                    </div>
                                                                                )}
                                                                                {!isPending && isExpenseType && mainTrx.expense_amount && (
                                                                                    <div className="text-muted x-small mt-1">
                                                                                        Total: {formatCurrency(mainTrx.expense_amount)}
                                                                                    </div>
                                                                                )}
                                                                            </td>
                                                                            <td className="pe-4 text-end py-4">
                                                                                <div className={`chevron-btn ${expandedInvoices.has(inv.id) ? 'is-active' : ''}`}>
                                                                                    <FiChevronRight />
                                                                                </div>
                                                                            </td>
                                                                        </tr>
                                                                        <tr>
                                                                            <td colSpan="5" className="p-0 border-0">
                                                                                <Collapse in={expandedInvoices.has(inv.id)}>
                                                                                    <div className="bg-light p-4 overflow-hidden" style={{ boxShadow: 'inset 0 2px 15px rgba(0,0,0,0.06)' }}>
                                                                                        <div className="d-flex align-items-center mb-4">
                                                                                            <h6 className="mb-0 fw-bold text-dark me-3">Details</h6>
                                                                                            <div className="flex-grow-1 border-bottom border-secondary border-opacity-25" style={{ height: '0px' }}></div>
                                                                                        </div>
                                                                                        {/* Inner Entry Details Logic Here */}
                                                                                        {renderNestedDetails(inv)}
                                                                                    </div>
                                                                                </Collapse>
                                                                            </td>
                                                                        </tr>
                                                                    </React.Fragment>
                                                                );
                                                            })}
                                                        </tbody>
                                                    </Table>
                                                </div>
                                            </Card>
                                        )}
                                    </div>
                                )}
                            </div>
                        </Tab>
                        {/* Ledger Statement Tab Removed as requested */}
                        {/* {!isLiabilityLedger && (
                            <Tab
                                eventKey="statement"
                                title={
                                    <span className="d-flex align-items-center">
                                        <FiFileText className="me-2" />
                                        Ledger Statement (Webzash)
                                    </span>
                                }
                            >
                                <div className="pt-3">
                                    {renderWebzashStatement()}
                                </div>
                            </Tab>
                        )} */}
                    </Tabs>
                </div>

                <style>{`
                    .backdrop-blur { backdrop-filter: blur(8px); border: 1px solid rgba(255,255,255,0.1); }
                    .bg-soft-blue { background-color: #f8fafc; }
                    .fw-black { font-weight: 800; }
                    .tracking-tighter { letter-spacing: -0.04em; }
                    .tracking-tight { letter-spacing: -0.02em; }
                    .ls-1 { letter-spacing: 0.05em; }
                    .x-small { font-size: 0.7rem; }
                    .text-lighter { color: #e2e8f0; }

                    /* Main Tabs */
                    .custom-main-tabs .nav-link { 
                        color: #64748b; font-weight: 700; font-size: 0.9rem; padding: 1rem 1.5rem; border: none !important; transition: all 0.3s ease; opacity: 0.6; position: relative;
                    }
                    .custom-main-tabs .nav-link:hover { opacity: 1; color: #3b82f6; }
                    .custom-main-tabs .nav-link.active { color: #3b82f6; opacity: 1; background: #fff !important; border-radius: 12px 12px 0 0; box-shadow: 0 -4px 15px rgba(0,0,0,0.03); }
                    .custom-main-tabs .nav-link.active::after { content: ''; position: absolute; bottom: 0; left: 15%; width: 70%; height: 3px; background: #3b82f6; border-radius: 3px; }

                    .border-bottom-soft { border-bottom: 1px solid #f1f5f9; }
                    .compact-ledger-table th { background: #1e293b !important; color: #f8fafc !important; }
                    .dashed { border-style: dashed !important; }

                    .stat-card { transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1); border: 1px solid #f1f5f9 !important; }
                    .stat-card:hover { transform: translateY(-4px); border-color: #e2e8f0 !important; }
                    .card-accent { position: absolute; top: 0; left: 0; width: 6px; height: 100%; border-radius: 4px 0 0 4px; }
                    .shadow-inner { box-shadow: inset 0 2px 4px 0 rgba(0, 0, 0, 0.05); }
                    
                    .ledger-table thead th { font-weight: 700; color: #64748b; background-color: #f8fafc; padding: 1.25rem 0.75rem; }
                    .ledger-row { border-bottom: 1px solid #f1f5f9; position: relative; transition: all 0.2s ease !important; }
                    .ledger-row:hover { background-color: #fff !important; transform: scale(1.005); box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.05), 0 4px 6px -2px rgba(0, 0, 0, 0.02); z-index: 10; }
                    .expanded-row { background-color: #f1f5f9 !important; }
                    
                    .timeline-col { position: relative; }
                    .timeline-point { position: absolute; left: 1.25rem; top: 50%; transform: translateY(-50%); width: 10px; height: 10px; background: #3b82f6; border: 2px solid #fff; border-radius: 50%; box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.1); z-index: 2; }
                    .date-primary { padding-left: 1.25rem; }
                    
                    .chevron-btn { width: 32px; height: 32px; border-radius: 50%; display: flex; align-items: center; justify-content: center; background: #f1f5f9; color: #64748b; transition: all 0.3s ease; }
                    .is-active { background: #3b82f6; color: #fff; transform: rotate(90deg); }
                    .text-primary-hover { color: #64748b; transition: all 0.2s; }
                    .text-primary-hover:hover { color: #3b82f6; }
                    
                    .last-border-0:last-child { border-bottom: none !important; }
                    .ledger-row-clean { transition: background-color 0.2s ease; }
                    .ledger-row-clean:hover { background-color: rgba(0,0,0,0.02); }
                    .target-ledger-row { background-color: #e0f2fe; border-left: 3px solid #0ea5e9; }
                    .compact-journal-card { border: 1px solid #e2e8f0 !important; box-shadow: 0 1px 3px rgba(0,0,0,0.02); transition: all 0.2s ease; }
                    
                    /* Custom Tabs Style */
                    .custom-ledger-tabs .nav-link { 
                        color: #64748b; font-weight: 700; font-size: 0.8rem; text-transform: uppercase; letter-spacing: 0.025em; padding: 0.75rem 1.25rem; border: none !important; transition: all 0.3s ease; position: relative; opacity: 0.7; 
                    }
                    .custom-ledger-tabs .nav-link:hover { opacity: 1; color: #3b82f6; }
                    .custom-ledger-tabs .nav-link.active { color: #3b82f6; opacity: 1; background: transparent !important; }
                    .custom-ledger-tabs .nav-link.active::after { content: ''; position: absolute; bottom: 0; left: 0; width: 100%; height: 3px; background: #3b82f6; border-radius: 3px; }
                    .custom-ledger-tabs .nav-link.disabled { opacity: 0.3; cursor: not-allowed; }
                    .compact-journal-card:hover { border-color: #cbd5e1 !important; transform: translateX(2px); }
                    .amount-badge { font-family: 'Helvetica Neue', Arial, sans-serif; font-weight: 700; }
                    
                    @keyframes slideUp { from { transform: translateY(10px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
                    .animate-slide-up { animation: slideUp 0.3s cubic-bezier(0.4, 0, 0.2, 1) forwards; }
                `}</style>

            </Offcanvas.Body>
        </Offcanvas >

        {/* Integration Modals - Moved outside to allow Ledger to hide/reopen automatically */}
        {showExpenseModal && (
            <ExpenseModal
                showModal={showExpenseModal}
                handleClose={() => setShowExpenseModal(false)}
                expenseId={selectedExpenseId}
                expenseData={selectedExpenseData}
                isEditMode={isEditMode}
                users={users}
                fetchUsers={fetchUsers}
                expenseTypes={expenseTypes}
                fetchExpenseTypes={fetchExpenseTypes}
            />
        )}

        {showPreviewModal && selectedInvoice && (
            <TaxInvoicePreview
                showModal={showPreviewModal}
                handleClose={() => {
                    setShowPreviewModal(false);
                    setSelectedQuotation(null);
                }}
                createdInvoice={selectedInvoice}
                quotationData={selectedQuotation}
                customIntegration={customIntegration}
                setCustomIntegration={setCustomIntegration}
                editableDeploymentIntegration={editableDeploymentIntegration}
                setEditableDeploymentIntegration={setEditableDeploymentIntegration}
                customIntegrationDescription={customIntegrationDescription}
                setCustomIntegrationDescription={setCustomIntegrationDescription}
                onDownloadPDF={handleDownloadTaxInvoicePDF}
            />
        )}

        <DocPreviewOffcanvas
            show={showDocOffcanvas}
            onHide={() => setShowDocOffcanvas(false)}
            url={docUrl}
            downloadUrl={downloadUrl}
            title={docTitle}
        />
    </>
);
};

const DocPreviewOffcanvas = ({ show, onHide, url, downloadUrl, title }) => (
    <Offcanvas show={show} onHide={onHide} placement="end" style={{ width: '1050px' }}>
        <Offcanvas.Header closeButton className="border-bottom bg-gradient py-2" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
            <div className="d-flex align-items-center justify-content-between w-100 pe-4">
                <Offcanvas.Title className="fw-bold fs-6 mb-0 text-white d-flex align-items-center gap-2">
                    <FiFileText size={18} />
                    <span className="text-truncate" style={{ maxWidth: '400px' }}>{title}</span>
                </Offcanvas.Title>
                <div className="d-flex gap-2">
                    <Button
                        variant="light"
                        size="sm"
                        className="d-flex align-items-center gap-2 shadow-sm rounded-pill px-3 fw-medium"
                        style={{ fontSize: '0.8rem' }}
                        onClick={() => window.open(url, '_blank')}
                    >
                    </Button>
                    <Button
                        variant="light"
                        size="sm"
                        className="d-flex align-items-center gap-2 shadow-sm rounded-pill px-3 fw-medium"
                        style={{ fontSize: '0.8rem' }}
                        onClick={async () => {
                            try {
                                const response = await axios.get(downloadUrl, {
                                    responseType: 'blob',
                                    headers: {
                                        Authorization: `Bearer ${JSON.parse(localStorage.getItem('user-info'))?.token}`,
                                        'Tenant-Origin': localStorage.getItem('user-origin')
                                    }
                                });
                                const fileName = title.replace(/[:\\/*?"<>|]/g, '_') + (url.endsWith('.pdf') ? '.pdf' : '');
                                saveAs(response.data, fileName);
                            } catch (error) {
                                console.error("Download error:", error);
                                window.open(url, '_blank');
                            }
                        }}
                    >
                        <FiDownload size={14} className="text-primary" />
                        <span className="text-primary">Download</span>
                    </Button>
                </div>
            </div>
        </Offcanvas.Header>
        <Offcanvas.Body className="p-0">
            {url ? (
                <iframe
                    src={`${url}#toolbar=0&navpanes=0&scrollbar=1`}
                    title={title}
                    width="100%"
                    height="100%"
                    style={{ border: 'none' }}
                    frameBorder="0"
                />
            ) : (
                <div className="d-flex align-items-center justify-content-center h-100">
                    <div className="text-center">
                        <div className="spinner-border text-primary mb-3" role="status"></div>
                        <p className="text-muted">Loading document...</p>
                    </div>
                </div>
            )}
        </Offcanvas.Body>
    </Offcanvas>
);

export default LedgerDetailsOffcanvas;
